import {
  BeachAsset,
  ImageDimensions,
  Configuration,
  ConfigurationType,
  BeachElementType,
  SelectedSpot,
  BeachElementImages,
} from '../jotai-modules/types'
import { BEACH_PLAN_ELEMENTS } from '../model/model'
import type { BookingData } from '../../../types/booking.types'
import type { SpotAvailabilityLock } from '../../../types/booking.types'
import { PaymentStatus } from '../../Payments/components/PaymentForm/model/model'

export const calculateBeachElementProps = (
  item: BeachAsset,
  numberOfRows: number,
  numberOfColumns: number,
  backgroundImageDimensions: ImageDimensions
) => {
  const containerTop = (item.top / numberOfRows) * backgroundImageDimensions.height - backgroundImageDimensions.height / numberOfRows

  const containerLeft = (item.left / numberOfColumns) * backgroundImageDimensions.width - backgroundImageDimensions.width / numberOfColumns

  return {
    containerTop,
    containerLeft,
    containerWidth: backgroundImageDimensions.width / numberOfColumns,
    containerHeight: backgroundImageDimensions.height / numberOfRows,
    images: {
      image: BEACH_PLAN_ELEMENTS?.[item.type]?.image,
      selectedImage: BEACH_PLAN_ELEMENTS?.[item.type]?.selectedImage,
      unavailableImage: BEACH_PLAN_ELEMENTS?.[item.type]?.unavailableImage,
      disabledImage: BEACH_PLAN_ELEMENTS?.[item.type]?.disabledImage,
    } as BeachElementImages,
    name: item.name,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getConfig = (data: [ConfigurationType], configuration: Configuration): any => {
  return data?.[0]?.[configuration]
}

export const getReservedSpotIds = (bookings: BookingData[] | undefined, exludedBookingId?: string): number[] => {
  const spotSet = new Set<number>()

  if (!bookings) return []
  // Get unique spots only for pending/successful bookings
  bookings
    .filter((booking) => booking.paymentStatus !== PaymentStatus.FAILED && booking.id !== exludedBookingId)
    .forEach((booking) => {
      booking.selectedSpotsIds.forEach((spot) => spotSet.add(spot))
    })

  return Array.from(spotSet)
}

export const getReservedSpotIdsFromLocks = (locks: SpotAvailabilityLock[] | undefined, exludedBookingId?: string): number[] => {
  const spotSet = new Set<number>()

  if (!locks) return []

  locks
    .filter((lock) => lock.tempBookingId !== exludedBookingId && (lock.status === 'pending' || lock.status === 'succeeded'))
    .forEach((lock) => spotSet.add(Number(lock.spotId)))

  return Array.from(spotSet)
}

export const isFacility = (type: BeachElementType) => {
  return (
    type === BeachElementType.WC ||
    type === BeachElementType.SHOWER ||
    type === BeachElementType.CHANGE_ROOM ||
    type === BeachElementType.LIFE_GUARD ||
    type === BeachElementType.BAR ||
    type === BeachElementType.JET_SKI ||
    type === BeachElementType.WINDSURF
  )
}

export const isRunway = (type: BeachElementType) => {
  const isRunwayHorizontal = type === BeachElementType.RUNWAY_HORIZONTAL
  const isRunwayVertical = type === BeachElementType.RUNWAY_VERTICAL
  const isRunwayCross = type === BeachElementType.RUNWAY_CROSS
  const isRunwayTLeft = type === BeachElementType.RUNWAY_T_LEFT
  const isRunwayTTop = type === BeachElementType.RUNWAY_T_TOP
  const isRunwayTRight = type === BeachElementType.RUNWAY_T_RIGHT
  const isRunwayTBottom = type === BeachElementType.RUNWAY_T_BOTTOM
  const isRunway = isRunwayHorizontal || isRunwayVertical || isRunwayCross || isRunwayTLeft || isRunwayTTop || isRunwayTRight || isRunwayTBottom
  return isRunway
}

export const getSpotProperties = (
  item: BeachAsset,
  selectedSpots: SelectedSpot[],
  alreadyReservedSpots: number[],
  images: BeachElementImages,
  isDisabled: boolean
) => {
  const isSelected = selectedSpots?.some((asset) => asset.id === item.id)
  const isReserved = alreadyReservedSpots?.some((spotId) => spotId === item.id)
  const isBookableSpot = [BeachElementType.UMBRELLA, BeachElementType.POUF, BeachElementType.CABANA].includes(item.type)
  const isPriceMissing = isBookableSpot && item.price == null
  const spotImage =
    isDisabled || isPriceMissing ? images.disabledImage : isReserved ? images.unavailableImage : isSelected ? images.selectedImage : images.image
  return {
    isReserved,
    isPriceMissing,
    spotImage,
    isRunway: isRunway(item.type),
    isFacility: isFacility(item.type),
  }
}
